import React, { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { Icon, Loading } from "source/shared/components"
import { useSession } from "source/shared/hooks/useSession"

const DirectoryInvitationAccept = () => {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [error, setError] = useState("")
  const [warning, setWarning] = useState("")
  const [shareableData, setShareableData] = useState(null)
  const [canViewDirectory, setCanViewDirectory] = useState(false)
  const [directoryUrl, setDirectoryUrl] = useState(null)
  const [loginUrl, setLoginUrl] = useState(null)
  const [reloginUrl, setReloginUrl] = useState(null)

  const {
    meta: { authenticated },
  } = useSession()

  useEffect(() => {
    sessionApiClient
      .post("/people/v2/accept_directory_invitation", {
        data: {
          attributes: {
            _e: searchParams.get("_e"),
          },
        },
      })
      .then((response) => {
        setLoading(false)
        setHasError(false)
        setShareableData(response.meta.shareable_data)
        setCanViewDirectory(response.meta.can_view_directory)
        setDirectoryUrl(response.meta.directory_url)
        setLoginUrl(response.meta.login_url)
        setReloginUrl(response.meta.relogin_url)
      })
      .catch(({ errors }) => {
        setLoading(false)
        setHasError(true)
        const errorDetail = errors?.[0]?.detail
        if (errorDetail.match(/warning:/))
          setWarning(errorDetail.replace(/warning:/, ""))
        else setError(errorDetail.replace(/error:/, ""))
      })
  }, [searchParams])

  const renderErrorMessage = () => {
    return error ? (
      <div className="alert error-alert d-f fd-c ai-c g-1">
        <Icon
          className="fs-0 c-ruby"
          symbol="general#exclamation-triangle"
          aria-hidden
        />
        <h2>Uh oh! Something is wrong.</h2>
        <p className="mb-0">{error}</p>
      </div>
    ) : warning ? (
      <div className="alert warning-alert d-f fd-c ai-c g-1">
        <Icon
          className="fs-0 c-citrine"
          symbol="general#exclamation-triangle"
          aria-hidden
        />
        <h2>Uh oh! Something is wrong.</h2>
        <p className="mb-0">{warning}</p>
      </div>
    ) : (
      <div className="alert error-alert d-f fd-c ai-c g-1">
        <Icon
          className="fs-0 c-ruby"
          symbol="general#exclamation-triangle"
          aria-hidden
        />
        <h2>Uh oh! That link wasn&#39;t valid.</h2>
        <p className="mb-0">Try copying the link directly from your email.</p>
      </div>
    )
  }

  const renderShareableData = () => {
    return (
      <div className="d-f mt-3">
        {shareableData.avatar && (
          <div style={{ maxWidth: "72px", marginRight: "16px" }}>
            <img
              style={{ borderRadius: "36px" }}
              width="72"
              height="72"
              className="img-round"
              src={shareableData.avatar}
              alt="avatar"
            />
          </div>
        )}
        <div>
          <p className="m-0">
            <strong>{shareableData.name}</strong>
          </p>
          {shareableData.email && <p className="m-0">{shareableData.email}</p>}
          {shareableData.phone_number && (
            <p className="m-0">{shareableData.phone_number}</p>
          )}
          {shareableData.address && (
            <p className="m-0">{shareableData.address}</p>
          )}
          {shareableData.birthday && (
            <p className="m-0">{shareableData.birthday}</p>
          )}
          {shareableData.anniversary && (
            <p className="m-0">{shareableData.anniversary}</p>
          )}
        </div>
      </div>
    )
  }

  return loading ? (
    <Loading />
  ) : hasError ? (
    renderErrorMessage()
  ) : (
    <div className="d-f fd-c">
      <div className="alert success-alert d-f jc-c ai-c g-1 fd-c">
        <Icon
          className="fs-3 c-emerald"
          symbol="general#check-circle"
          aria-hidden
        />
        <h2>Thanks for sharing your information!</h2>
        <p className="mb-0">
          The following details are now visible to others in the directory.
        </p>
      </div>
      {renderShareableData()}
      <div
        style={{
          marginTop: "16px",
          paddingTop: "16px",
          borderTop: "1px, solid var(--color-tint5)",
        }}
      >
        {authenticated && canViewDirectory ? (
          <span>
            <a href={`${directoryUrl}`}>View the directory</a> to customize
            which information is shared or to share information about others in
            household.
          </span>
        ) : authenticated ? (
          <span>
            If you would like to customize which information is shared, share
            information about others in your household, or view the directory,{" "}
            <a href={`${reloginUrl}`}>please log in to Church Center.</a>
          </span>
        ) : (
          <span>
            If you would like to customize which information is shared, share
            information about others in your household, or view the directory,{" "}
            <a href={`${loginUrl}`}>please log in to Church Center.</a>
          </span>
        )}
      </div>
    </div>
  )
}

export default DirectoryInvitationAccept
